"use client";
import React, { useEffect, useRef, useState } from "react";
import { observer } from "mobx-react-lite";
import { useStore } from "../services/store";

import {
  AllSymbols,
  isETF,
  isRealETF,
  SectorMap,
  findMatchingSectorSymbols,
  sortBySectorOrder,
} from "./symbols";
import StockMiniCell from "./StockMinCell";
import StrongSymbolRow from "./StrongSymbolRow";
import SymbolIframe from "./SymbolIframe";
import { formatPattern } from "./util-filter";
import {
  sortSectorSymbolsByPercentChange,
  getBestSectorForSymbol,
  groupSymbolsBySector,
  sortGroupsByStrength,
  filterSymbolsByPercentageThreshold,
} from "./util-symbol-grouping";

const CARD_WIDTH = 120; // px (matches your gridTemplateColumns cell width)
const GAP = 6; // px (matches your gap)

export default observer(function StockGrid({
  basePath = "/csv-adv",
  source = "all",
  sortBySector = false,
}) {
  const store = useStore();
  const { formattedDate: date } = store;
  const [loading, setLoading] = useState(false);

  const gridRef = useRef(null);
  const [cols, setCols] = useState(1); // number of columns currently in the grid
  const [expandedSymbol, setExpandedSymbol] = useState(null);
  const [expandedIndex, setExpandedIndex] = useState(-1);

  //const symbols = SectorMap[store.sector] || AllSymbols;

  const favoriteSymbols = store.favoriteListSorted.slice();
  const symbols =
    source === "hotlist"
      ? favoriteSymbols
      : findMatchingSectorSymbols(store.sector);

  const symbolsKey = symbols.join("|");
  // Measure grid width → derive current columns count
  useEffect(() => {
    const el = gridRef.current;
    if (!el) return;

    const calcCols = () => {
      const w = el.clientWidth || 0;
      const c = Math.max(1, Math.floor((w + GAP) / (CARD_WIDTH + GAP)));
      setCols(c);
    };

    calcCols();
    const ro = new ResizeObserver(calcCols);
    ro.observe(el);
    window.addEventListener("resize", calcCols);

    return () => {
      ro.disconnect();
      window.removeEventListener("resize", calcCols);
    };
  }, []);

  // Load CSVs (same logic you had)
  useEffect(() => {
    if (!store.currentGridMap) {
      // wait until currentGridMap is loaded
      return;
    }

    const ctrl = new AbortController();

    (async () => {
      try {
        // ✅ upfront check only for "aapl"
        const cached = store.currentGridMap?.["aapl"];
        //console.log("AAAcached0", date, cached);
        if (date && cached?.[0]?.timestamp === date) {
          //console.log("AAAcached", date);
          store.setStockGridMap(store.currentGridMap);
          setLoading(false);
          return;
        }

        // otherwise fall back to old way when date back
        const tasks = symbols.map(async (sym) => {
          const url = `${basePath.replace(/\/$/, "")}/${sym.toLowerCase()}.csv`;
          try {
            const res = await fetch(url, {
              signal: ctrl.signal,
              cache: "no-store",
              headers: { Accept: "text/csv" },
            });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const text = await res.text();
            const ticks = window.d3.csvParse(text, (d) => ({
              date: d.date || d.timestamp,
              open: d.open ? +d.open : undefined,
              high: d.high ? +d.high : undefined,
              low: d.low ? +d.low : undefined,
              close:
                d.close || d.adjClose ? +d.close || +d.adjClose : undefined,
              volume: d.volume ? +d.volume : undefined,
              timestamp: d.timestamp,
              marketCap: d.marketCap,
            }));
            return [sym, ticks];
          } catch {
            return [sym, []];
          }
        });

        const pairs = await Promise.all(tasks);
        const map = {};

        for (const [sym, ticks] of pairs) {
          let today, prev;
          if (date && ticks.length) {
            const idx = ticks.findIndex(
              (t) => t.timestamp === date || t.date === date,
            );
            if (idx >= 0) {
              today = ticks[idx];
              prev = ticks[idx + 1]; // next element in newest-first order
            }
          } else {
            today = ticks[0];
            prev = ticks[1];
          }
          map[sym] = [today, prev].filter(Boolean);
        }

        if (!ctrl.signal.aborted) {
          store.setStockGridMap(map);
          setLoading(false);

          //console.log("AAAGrid", map);
        }
      } catch {
        if (!ctrl.signal.aborted) setLoading(false);
      }
    })();

    return () => ctrl.abort();
  }, [symbolsKey, basePath, date, store.currentGridMap]);

  useEffect(() => {
    setExpandedSymbol(null);
    setExpandedIndex(-1);
  }, [symbolsKey]);

  const gridStyle = {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, 120px)",
    gap: GAP,
    justifyContent: "space-between",
  };

  const handleCellClick = (sym, idx) => {
    if (expandedSymbol === sym) {
      setExpandedSymbol(null);
      setExpandedIndex(-1);
    } else {
      setExpandedSymbol(sym);
      setExpandedIndex(idx);
    }
  };

  /*
  const sortedSymbols = symbols.slice().sort((a, b) => {
    const capA = store.gridMap?.[a]?.[0]?.marketCap ?? 0;
    const capB = store.gridMap?.[b]?.[0]?.marketCap ?? 0;
    return capB - capA; // 降序
  });
  */
  // 假设你有 isETF(symbol) 函数
  const sortedSymbols =
    source === "hotlist"
      ? symbols
      : store.sector === "etf"
        ? symbols
        : symbols.slice().sort((a, b) => {
            const aIsETF = isRealETF(a.toLowerCase()) ? 1 : 0;
            const bIsETF = isRealETF(b.toLowerCase()) ? 1 : 0;
            const capA = store.marketCapMap?.[a.toLowerCase()] ?? 0;
            const capB = store.marketCapMap?.[b.toLowerCase()] ?? 0;
            if (aIsETF !== bIsETF) return bIsETF - aIsETF;
            return capB - capA;
          });

  // Compute where to insert the detail panel (after the end of the clicked row)
  const rowEndIndex =
    expandedIndex >= 0
      ? Math.min(
          (Math.floor(expandedIndex / cols) + 1) * cols - 1,
          sortedSymbols.length - 1,
        )
      : -1;

  // Build grid items interleaving the detail row at the proper spot
  const items = [];

  sortedSymbols.forEach((sym, i) => {
    items.push(
      <div
        key={`cell-${sym}`}
        role="button"
        tabIndex={0}
        onClick={() => handleCellClick(sym, i)}
        onKeyDown={(e) =>
          (e.key === "Enter" || e.key === " ") && handleCellClick(sym, i)
        }
        style={{ cursor: "pointer" }}
      >
        <StockMiniCell symbol={sym} />
      </div>,
    );

    if (expandedSymbol && i === rowEndIndex) {
      const pattern =
        (!store.isPercent && store.patternMap?.[expandedSymbol]) || "";
      //console.log("AAApatternMap", expandedSymbol, pattern);
      items.push(
        <div key={`detail-${expandedSymbol}`} style={{ gridColumn: "1 / -1" }}>
          <div
            style={{
              margin: "8px 0 12px",
              background: "#fff",
              border: "1px solid #e5e7eb",
              borderRadius: 10,
              boxShadow: "0 8px 30px rgba(0,0,0,.08)",
              padding: 8,
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                padding: "4px 6px 8px",
                justifyContent: "space-between",
              }}
            >
              <div className="flex items-center gap2">
                <div className="f6 code dark-gray mr2">
                  {expandedSymbol.toUpperCase()}
                </div>

                {pattern && (
                  <span className="f7 mid-gray o-30 fw5">
                    {formatPattern(pattern)}
                  </span>
                )}
              </div>

              <button
                onClick={() => {
                  setExpandedSymbol(null);
                  setExpandedIndex(-1);
                }}
                style={{
                  fontSize: 12,
                  color: "#475569",
                  padding: "2px 6px",
                  borderRadius: 6,
                  border: "1px solid #e5e7eb",
                  background: "#fff",
                  cursor: "pointer",
                }}
              >
                Close
              </button>
            </div>

            {/* The detail content */}
            <SymbolIframe symbol={expandedSymbol.toLowerCase()} />
          </div>
        </div>,
      );
    }
  });

  if (!symbols.length) {
    return <div style={{ color: "#6b7280", fontSize: 13 }}>No symbols.</div>;
  }

  const showAsRows = source === "hotlist" || store.showStockGridAsRows;

  // Toggle switches component
  const ToggleSwitches = () =>
    source === "hotlist" ? null : (
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          gap: 8,
          marginBottom: 12,
        }}
      >
      {/* Group toggle - only show in row mode */}
      {store.showStockGridAsRows && (
        <button
          onClick={() => store.toggleStockGridGrouped()}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            background: "rgba(0,0,0,0.1)",
            border: "1px solid rgba(255,255,255,0.2)",
            borderRadius: 6,
            padding: "6px 12px",
            color: "#e2e8f0",
            fontSize: 12,
            cursor: "pointer",
            transition: "all 0.2s ease",
            opacity: store.showStockGridGrouped ? 1 : 0.4,
          }}
          title={
            store.showStockGridGrouped
              ? "Ungroup symbols"
              : "Group symbols by sector"
          }
        >
          <span>{store.showStockGridGrouped ? "🗂️" : "📄"}</span>
          <span>Grouped</span>
        </button>
      )}

      {/* Grid/Row mode toggle */}
      <button
        onClick={() => store.toggleStockGridMode()}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          background: "rgba(0,0,0,0.1)",
          border: "1px solid rgba(255,255,255,0.2)",
          borderRadius: 6,
          padding: "6px 12px",
          color: "#e2e8f0",
          fontSize: 12,
          cursor: "pointer",
          transition: "all 0.2s ease",
          opacity: store.showStockGridAsRows ? 0.4 : 1,
        }}
        title={
          store.showStockGridAsRows
            ? "Switch to Grid View"
            : "Switch to Row View"
        }
      >
        <span>{store.showStockGridAsRows ? "📋" : "⚏"}</span>
        <span>Grid</span>
      </button>
      </div>
    );

  // If showStockGridAsRows is true, show symbol rows instead of grid
  if (showAsRows) {
    const symbolsWithData = sortedSymbols.map((sym) => {
      const pattern = store.patternMap?.[sym] || "";
      const sector = getBestSectorForSymbol(sym, store.sector);
      return [sym, { pattern, sector }];
    });

    const rawSortedByPercentage = sortSectorSymbolsByPercentChange(
      symbolsWithData,
      store.currentGridMap,
      store.formattedDate,
      store.strongSort, // use strongSort as sort direction
    );

    // Apply percentage filter if enabled
    const sortedByPercentage = store.filterByPercentageThreshold
      ? filterSymbolsByPercentageThreshold(
          rawSortedByPercentage,
          store.currentGridMap,
          store.formattedDate,
          5,
        )
      : rawSortedByPercentage;

    return (
      <div>
        <ToggleSwitches />

        {loading ? (
          <div style={{ color: "#6b7280", fontSize: 13, marginBottom: 8 }}>
            Loading…
          </div>
        ) : null}

        <div
          className="font-mono text-sm whitespace-pre leading-5 near-white bg-near-black"
          style={{
            marginBottom: 8,
            borderLeft: `3px solid rgba(34,197,94,.9)`,
            padding: 8,
            paddingLeft: 12,
            borderTopLeftRadius: 8,
            borderBottomLeftRadius: 8,
            backgroundColor: "rgba(20,83,45,.3)",
            boxShadow: "0 2px 6px rgba(0,0,0,.25)",
          }}
        >
          {source === "hotlist" || store.showStockGridGrouped
            ? // Group by sector with spacing between groups
              (() => {
                const grouped = groupSymbolsBySector(
                  sortedByPercentage,
                  source === "hotlist" ? "" : store.sector,
                );
                const groupEntries =
                  source === "hotlist" && sortBySector
                    ? sortBySectorOrder(
                        Object.entries(grouped),
                        ([sectorKey]) => sectorKey,
                      )
                    : sortGroupsByStrength(grouped);

                return groupEntries.map(
                  ([sectorKey, sectorSymbols], groupIndex) => (
                    <div key={sectorKey}>
                      {groupIndex > 0 && <div style={{ height: 8 }} />}
                      {/* Group name header */}
                      <div
                        style={{
                          fontSize: 11,
                          fontWeight: 600,
                          color: "rgba(255,255,255,0.6)",
                          textTransform: "uppercase",
                          letterSpacing: "0.5px",
                          marginBottom: 4,
                          paddingLeft: 4,
                          borderLeft: "2px solid rgba(255,255,255,0.2)",
                        }}
                      >
                        {sectorKey}
                      </div>
                      {/* Symbols within this group sorted by percentage */}
                      {sortSectorSymbolsByPercentChange(
                        sectorSymbols,
                        store.currentGridMap,
                        store.formattedDate,
                        store.strongSort, // use strongSort as sort direction
                      ).map(([sym, valObj]) => (
                        <StrongSymbolRow
                          key={sym}
                          sym={sym}
                          val={valObj}
                          isPos={true}
                          colCh={10}
                          source={source}
                        />
                      ))}
                    </div>
                  ),
                );
              })()
            : // Simple list without grouping
              sortedByPercentage.map(([sym, valObj]) => (
                <StrongSymbolRow
                  key={sym}
                  sym={sym}
                  val={valObj}
                  isPos={true}
                  colCh={10}
                  source={source}
                />
              ))}
        </div>
      </div>
    );
  }

  return (
    <div>
      <ToggleSwitches />

      {loading ? (
        <div style={{ color: "#6b7280", fontSize: 13, marginBottom: 8 }}>
          Loading…
        </div>
      ) : null}

      <div ref={gridRef} style={gridStyle}>
        {items}
      </div>
    </div>
  );
});
